import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProfilePictureUpload } from "@/components/ProfilePictureUpload";
import { childAgeMonths, withAge } from "@/lib/childAge";
import { Minus, Plus } from "lucide-react";
import { useWorkspaceMode } from "@/hooks/useWorkspaceMode";
import { toast } from "sonner";

const DISTRICTS = ["Balham", "Tooting", "Clapham", "Battersea", "Wandsworth", "Streatham", "Brixton"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const thisYear = new Date().getFullYear();
const YEARS = Array.from({ length: 19 }, (_, i) => String(thisYear - i));

type Child = { id: string; birth_ym: string | null };

interface Props {
  userId: string;
  firstName: string;
  initial: { district: string | null; avatar_url: string | null; open_to_meet: boolean; children_info: any; is_pregnant?: boolean };
  isDual?: boolean;
  onDone: () => void;
}

const TOTAL = 4;

export const WelcomeWizard = ({ userId, firstName, initial, isDual, onDone }: Props) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { setMode } = useWorkspaceMode();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [district, setDistrict] = useState(initial.district || "");
  const [customArea, setCustomArea] = useState(initial.district && !DISTRICTS.includes(initial.district) ? initial.district : "");
  const isOther = district !== "" && !DISTRICTS.includes(district);
  const [avatar, setAvatar] = useState<string | null>(initial.avatar_url);
  const [openToMeet, setOpenToMeet] = useState(true);
  const [pregnant, setPregnant] = useState(Boolean(initial.is_pregnant));
  const [children, setChildren] = useState<Child[]>(() => {
    const list: Child[] = Array.isArray(initial.children_info)
      ? initial.children_info.map((c: any) => ({ id: c.id || crypto.randomUUID(), birth_ym: c.birth_ym ?? null }))
      : [];
    return list.length ? list : [{ id: crypto.randomUUID(), birth_ym: null }];
  });

  const save = async (updates: Record<string, unknown>) => {
    setSaving(true);
    const { error } = await supabase.from("profiles").update(updates as any).eq("id", userId);
    setSaving(false);
    if (error) {
      toast.error("Couldn't save that, please try again");
      return false;
    }
    qc.invalidateQueries({ queryKey: ["user-profile", userId] });
    return true;
  };

  const next = () => setStep((s) => s + 1);

  const saveDistrict = async () => {
    const area = isOther ? customArea.trim() : district;
    if (!area) return;
    if (await save({ district: area })) next();
  };
  const saveChildren = async () => {
    const valid = children.filter((c) => c.birth_ym);
    const payload = valid.map((c) => withAge({ id: c.id, birth_ym: c.birth_ym }));
    if (await save({ children_info: payload, is_pregnant: pregnant })) next();
  };
  const saveOpenToMeet = async () => { if (await save({ open_to_meet: openToMeet })) next(); };

  const finish = async (to: string) => {
    await save({ onboarding_completed_at: new Date().toISOString(), onboarding_tour_seen: true });
    onDone();
    navigate(to, { replace: true });
  };

  const finishToMember = async () => {
    setMode("member");
    await finish("/activities");
  };

  const finishToBusiness = async () => {
    await save({ onboarding_completed_at: new Date().toISOString(), onboarding_tour_seen: true });
    setMode("business");
    onDone();
    navigate("/business", { replace: true });
  };

  const setCount = (n: number) => {
    n = Math.max(0, Math.min(5, n));
    setChildren((cur) =>
      n > cur.length
        ? [...cur, ...Array.from({ length: n - cur.length }, () => ({ id: crypto.randomUUID(), birth_ym: null }))]
        : cur.slice(0, n)
    );
  };
  const setBirth = (id: string, part: "y" | "m", v: string) =>
    setChildren((cur) =>
      cur.map((c) => {
        if (c.id !== id) return c;
        const [y, m] = (c.birth_ym || "-").split("-");
        const ny = part === "y" ? v : y;
        const nm = part === "m" ? v : m;
        return { ...c, birth_ym: ny && nm ? `${ny}-${nm}` : `${ny || ""}-${nm || ""}` };
      })
    );
  const childrenComplete = children.every((c) => /^\d{4}-\d{2}$/.test(c.birth_ym || "")) && (pregnant || children.length > 0);

  const BackLink = () => (
    <button onClick={() => setStep((s) => s - 1)} className="min-h-11 text-sm text-muted-foreground underline">
      Back
    </button>
  );

  const Skip = () => (
    <button onClick={next} className="min-h-11 text-sm text-muted-foreground underline">
      Skip for now
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-background sm:bg-gradient-subtle sm:items-center sm:justify-center sm:p-6 overflow-y-auto"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to Balham Babes"
    >
      <div className="mx-auto w-full max-w-md sm:max-w-xl flex-1 sm:flex-none sm:min-h-[560px] flex flex-col px-5 py-6 sm:p-10 sm:rounded-2xl sm:bg-card sm:shadow-soft sm:border">
        {step < TOTAL && (
          <div className="mb-8">
            <p className="text-xs font-medium text-muted-foreground mb-2">Step {step + 1} of {TOTAL}</p>
            <Progress value={((step + 1) / TOTAL) * 100} className="h-2" />
          </div>
        )}

        {step === 0 && (
          <div className="flex-1 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Welcome, {firstName}! 👋</h1>
              <p className="mt-2 text-muted-foreground">Which area are you in? We'll show you what's on nearby and other parents and carers close to you.</p>
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Choose your area">
              {DISTRICTS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDistrict(d)}
                  aria-pressed={district === d}
                  className={`min-h-11 px-4 rounded-full text-base font-medium border transition-colors ${
                    district === d
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground border-input hover:bg-muted/50"
                  }`}
                >
                  {d}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDistrict("other")}
                aria-pressed={isOther}
                className={`min-h-11 px-4 rounded-full text-base font-medium border transition-colors ${
                  isOther
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-input hover:bg-muted/50"
                }`}
              >
                Other
              </button>
            </div>
            {isOther && (
              <Input
                className="h-12 text-base"
                placeholder="Tell us your area"
                autoFocus
                value={customArea}
                onChange={(e) => setCustomArea(e.target.value)}
                maxLength={60}
              />
            )}
            <div className="mt-auto space-y-2">
              <Button size="lg" className="w-full" disabled={!district || (isOther && !customArea.trim()) || saving} onClick={saveDistrict}>Continue</Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="flex-1 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Your little ones</h1>
              <p className="mt-2 text-muted-foreground">How many children do you have or look after? Their age helps other parents and carers find playdates, and it updates automatically.</p>
            </div>
            <div className="flex items-center justify-center gap-6">
              <Button variant="outline" size="icon" className="h-12 w-12 rounded-full" onClick={() => setCount(children.length - 1)} aria-label="One fewer child"><Minus /></Button>
              <span className="text-4xl font-bold tabular-nums w-10 text-center">{children.length}</span>
              <Button variant="outline" size="icon" className="h-12 w-12 rounded-full" onClick={() => setCount(children.length + 1)} aria-label="One more child"><Plus /></Button>
            </div>
            <label htmlFor="wiz-pregnant" className="flex items-center justify-between gap-4 rounded-xl border border-primary/40 bg-primary/5 p-4 cursor-pointer">
              <span className="text-base font-medium">I'm pregnant 👶</span>
              <Switch id="wiz-pregnant" checked={pregnant} onCheckedChange={setPregnant} />
            </label>
            <div className="space-y-3">
              {children.map((c, i) => {
                const [y, m] = (c.birth_ym || "-").split("-");
                const ok = /^\d{4}-\d{2}$/.test(c.birth_ym || "");
                return (
                  <div key={c.id} className="rounded-xl border p-3 space-y-2">
                    <p className="text-sm font-medium">Child {i + 1} – born in{ok ? ` (${Math.floor(childAgeMonths(c) / 12) >= 1 ? `${Math.floor(childAgeMonths(c) / 12)}y ` : ""}${childAgeMonths(c) % 12}m)` : ""}</p>
                    <div className="flex gap-2">
                      <Select value={m || undefined} onValueChange={(v) => setBirth(c.id, "m", v)}>
                        <SelectTrigger className="flex-1 h-11" aria-label="Birth month"><SelectValue placeholder="Month" /></SelectTrigger>
                        <SelectContent className="z-[110]">{MONTHS.map((l, idx) => <SelectItem key={l} value={String(idx + 1).padStart(2, "0")}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={y || undefined} onValueChange={(v) => setBirth(c.id, "y", v)}>
                        <SelectTrigger className="flex-1 h-11" aria-label="Birth year"><SelectValue placeholder="Year" /></SelectTrigger>
                        <SelectContent className="z-[110]">{YEARS.map((yr) => <SelectItem key={yr} value={yr}>{yr}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-auto space-y-2">
              {children.length === 0 && !pregnant && (
                <p className="text-sm text-muted-foreground text-center">Add at least one child, or tick "I'm pregnant" to continue.</p>
              )}
              <Button size="lg" className="w-full" disabled={saving || !childrenComplete} onClick={saveChildren}>Continue</Button>
              <div className="flex items-center justify-between">
                <BackLink />
                <Skip />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex-1 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Add a photo</h1>
              <p className="mt-2 text-muted-foreground">A friendly photo helps other parents and carers recognise you at activities.</p>
            </div>
            <div className="flex justify-center py-4">
              <ProfilePictureUpload userId={userId} currentAvatarUrl={avatar} firstName={firstName} inline onAvatarUpdate={(u) => { setAvatar(u); qc.invalidateQueries({ queryKey: ["user-profile", userId] }); }} />
            </div>
            <div className="mt-auto space-y-2">
              <Button size="lg" className="w-full" disabled={!avatar} onClick={next}>Continue</Button>
              <div className="flex items-center justify-between">
                <BackLink />
                <Skip />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="flex-1 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Open to meet?</h1>
              <p className="mt-2 text-muted-foreground">Appear in "Open to meet today" so nearby parents and carers can say hi. You can change this anytime on your profile.</p>
            </div>
            <label htmlFor="wiz-otm" className="flex items-center justify-between gap-4 rounded-xl border border-primary/40 bg-primary/5 p-4 cursor-pointer">
              <span className="text-base font-medium">I'm open to meet other local parents and carers</span>
              <Switch id="wiz-otm" checked={openToMeet} onCheckedChange={setOpenToMeet} />
            </label>
            <div className="mt-auto space-y-2">
              <Button size="lg" className="w-full" disabled={saving} onClick={saveOpenToMeet}>Continue</Button>
              <div className="flex items-center justify-between">
                <BackLink />
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <div className="text-6xl">🎉</div>
            <h1 className="text-2xl font-bold text-foreground">You're all set, {firstName}!</h1>
            <p className="text-muted-foreground">We've got the basics so you can see what's happening near you.</p>
            <p className="text-sm text-muted-foreground rounded-xl bg-primary/5 p-4">
              Tip: you can add more to your profile anytime – a short bio, work status, languages and more – to help other parents and carers get to know you.
            </p>
            {isDual ? (
              <div className="w-full space-y-2 mt-4">
                <p className="text-sm font-medium text-foreground">You also have a business profile – set it up now?</p>
                <Button size="lg" className="w-full" disabled={saving} onClick={finishToBusiness}>Set up my business profile</Button>
                <Button size="lg" variant="outline" className="w-full" disabled={saving} onClick={finishToMember}>Maybe later – explore activities</Button>
              </div>
            ) : (
              <div className="w-full space-y-2 mt-4">
                <Button size="lg" className="w-full" disabled={saving} onClick={() => finish("/activities")}>Explore activities</Button>
                <Button size="lg" variant="outline" className="w-full" disabled={saving} onClick={() => finish(`/user/${userId}`)}>Complete my profile</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomeWizard;
